import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Navigation } from '@/components/Navigation';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { Star, MapPin, DollarSign, Calendar, Instagram, Facebook, Youtube, MessageCircle } from 'lucide-react';
import { categories } from '@/utils/categories';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${API_URL}/api`;

const ArtistDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [artist, setArtist] = useState(null);
  const [loading, setLoading] = useState(true);
  const [bookingDialogOpen, setBookingDialogOpen] = useState(false);
  const [bookingData, setBookingData] = useState({
    booking_type: 'inquiry',
    event_date: '',
    event_type: '',
    event_location: '',
    guest_count: '',
    duration_hours: '',
    special_requests: '',
  });

  useEffect(() => {
    fetchArtist();
  }, [id]);

  const fetchArtist = async () => {
    try {
      const response = await axios.get(`${API}/artists/${id}`);
      setArtist(response.data);
    } catch (error) {
      console.error('Failed to fetch artist:', error);
      toast.error('Artist not found');
    } finally {
      setLoading(false);
    }
  };

  const handleBooking = async (e) => {
    e.preventDefault();
    if (!user) {
      toast.error('Please login to book');
      navigate('/login');
      return;
    }

    if (user.role !== 'customer') {
      toast.error('Only customers can book artists');
      return;
    }

    try {
      await axios.post(`${API}/bookings`, {
        ...bookingData,
        artist_id: id,
        guest_count: bookingData.guest_count ? parseInt(bookingData.guest_count) : null,
        duration_hours: bookingData.duration_hours ? parseInt(bookingData.duration_hours) : null,
      });
      
      toast.success(
        bookingData.booking_type === 'instant'
          ? 'Booking confirmed!'
          : 'Inquiry sent successfully!'
      );
      setBookingDialogOpen(false);
      navigate('/dashboard/customer');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Booking failed');
    }
  };

  const handleMessage = () => {
    if (!user) {
      toast.error('Please login to message');
      navigate('/login');
      return;
    }
    navigate(`/dashboard/${user.role}/messages?user=${artist.user_id}`);
  };

  if (loading) {
    return (
      <div className="min-h-screen">
        <Navigation />
        <div className="flex items-center justify-center py-20">
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (!artist) {
    return (
      <div className="min-h-screen">
        <Navigation />
        <div className="flex items-center justify-center py-20">
          <p className="text-gray-600">Artist not found</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white" data-testid="artist-detail-page">
      <Navigation />

      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 py-12">
        <div className="grid lg:grid-cols-3 gap-12">
          <div className="lg:col-span-2 space-y-8">
            <div>
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h1 className="text-4xl font-semibold mb-2" data-testid="artist-name">
                    {artist.artist_name}
                  </h1>
                  <p className="text-xl text-gray-600 capitalize">
                    {categories[artist.category]?.name || artist.category}
                    {artist.subcategory && ` • ${artist.subcategory}`}
                  </p>
                </div>
                {artist.rating > 0 && (
                  <div className="flex items-center space-x-2 bg-purple-50 px-4 py-2 rounded-full">
                    <Star className="w-5 h-5 text-yellow-500 fill-yellow-500" />
                    <span className="text-lg font-semibold">{artist.rating}</span>
                    <span className="text-sm text-gray-600">({artist.reviews_count})</span>
                  </div>
                )}
              </div>

              <div className="flex flex-wrap gap-4 text-gray-600">
                <div className="flex items-center space-x-2">
                  <MapPin className="w-5 h-5" />
                  <span>{artist.location}</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Calendar className="w-5 h-5" />
                  <span>{artist.experience_years} years experience</span>
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <h2 className="text-2xl font-semibold">About</h2>
              <p className="text-gray-600 leading-relaxed">{artist.bio}</p>
            </div>

            {artist.skills && artist.skills.length > 0 && (
              <div className="space-y-4">
                <h2 className="text-2xl font-semibold">Skills</h2>
                <div className="flex flex-wrap gap-2">
                  {artist.skills.map((skill, idx) => (
                    <Badge key={idx} variant="secondary" className="px-3 py-1">
                      {skill}
                    </Badge>
                  ))}
                </div>
              </div>
            )}

            {artist.portfolio_images && artist.portfolio_images.length > 0 && (
              <div className="space-y-4">
                <h2 className="text-2xl font-semibold">Portfolio</h2>
                <div className="masonry-grid">
                  {artist.portfolio_images.map((img, idx) => (
                    <div key={idx} className="masonry-item">
                      <img
                        src={img}
                        alt={`Portfolio ${idx + 1}`}
                        className="w-full rounded-lg"
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}

            {artist.reviews && artist.reviews.length > 0 && (
              <div className="space-y-6">
                <h2 className="text-2xl font-semibold">Reviews</h2>
                <div className="space-y-4">
                  {artist.reviews.map((review) => (
                    <div key={review.id} className="bg-gray-50 rounded-xl p-6">
                      <div className="flex items-center justify-between mb-3">
                        <div className="font-semibold">{review.customer_name}</div>
                        <div className="flex items-center space-x-1">
                          {[...Array(5)].map((_, i) => (
                            <Star
                              key={i}
                              className={`w-4 h-4 ${
                                i < review.rating
                                  ? 'text-yellow-500 fill-yellow-500'
                                  : 'text-gray-300'
                              }`}
                            />
                          ))}
                        </div>
                      </div>
                      <p className="text-gray-600">{review.comment}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="lg:col-span-1">
            <div className="sticky top-24 bg-white border border-gray-100 rounded-2xl p-6 shadow-sm space-y-6">
              <div>
                <div className="text-sm text-gray-600 mb-2">Starting at</div>
                <div className="text-3xl font-bold text-purple-600">
                  ${artist.price_starting}
                </div>
              </div>

              <Dialog open={bookingDialogOpen} onOpenChange={setBookingDialogOpen}>
                <DialogTrigger asChild>
                  <Button
                    className="w-full h-12 rounded-full bg-purple-600 hover:bg-purple-700"
                    data-testid="book-now-button"
                  >
                    Book Now
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle>Book {artist.artist_name}</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleBooking} className="space-y-4">
                    <div className="space-y-2">
                      <Label>Booking Type</Label>
                      <RadioGroup
                        value={bookingData.booking_type}
                        onValueChange={(value) =>
                          setBookingData({ ...bookingData, booking_type: value })
                        }
                      >
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem value="inquiry" id="inquiry" data-testid="booking-type-inquiry" />
                          <Label htmlFor="inquiry" className="cursor-pointer">
                            Send Inquiry
                          </Label>
                        </div>
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem value="instant" id="instant" data-testid="booking-type-instant" />
                          <Label htmlFor="instant" className="cursor-pointer">
                            Instant Booking
                          </Label>
                        </div>
                      </RadioGroup>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="event_date">Event Date</Label>
                      <Input
                        id="event_date"
                        type="date"
                        value={bookingData.event_date}
                        onChange={(e) =>
                          setBookingData({ ...bookingData, event_date: e.target.value })
                        }
                        required
                        data-testid="event-date-input"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="event_type">Event Type</Label>
                      <Input
                        id="event_type"
                        value={bookingData.event_type}
                        onChange={(e) =>
                          setBookingData({ ...bookingData, event_type: e.target.value })
                        }
                        placeholder="Wedding, Birthday, Corporate..."
                        required
                        data-testid="event-type-input"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="event_location">Event Location</Label>
                      <Input
                        id="event_location"
                        value={bookingData.event_location}
                        onChange={(e) =>
                          setBookingData({ ...bookingData, event_location: e.target.value })
                        }
                        placeholder="City, State"
                        required
                        data-testid="event-location-input"
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="guest_count">Guest Count</Label>
                        <Input
                          id="guest_count"
                          type="number"
                          value={bookingData.guest_count}
                          onChange={(e) =>
                            setBookingData({ ...bookingData, guest_count: e.target.value })
                          }
                          placeholder="100"
                          data-testid="guest-count-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="duration_hours">Duration (hrs)</Label>
                        <Input
                          id="duration_hours"
                          type="number"
                          value={bookingData.duration_hours}
                          onChange={(e) =>
                            setBookingData({ ...bookingData, duration_hours: e.target.value })
                          }
                          placeholder="2"
                          data-testid="duration-input"
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="special_requests">Special Requests</Label>
                      <Textarea
                        id="special_requests"
                        value={bookingData.special_requests}
                        onChange={(e) =>
                          setBookingData({ ...bookingData, special_requests: e.target.value })
                        }
                        placeholder="Any special requirements..."
                        rows={3}
                        data-testid="special-requests-input"
                      />
                    </div>

                    <Button
                      type="submit"
                      className="w-full bg-purple-600 hover:bg-purple-700"
                      data-testid="submit-booking-button"
                    >
                      {bookingData.booking_type === 'instant' ? 'Confirm Booking' : 'Send Inquiry'}
                    </Button>
                  </form>
                </DialogContent>
              </Dialog>

              <Button
                variant="outline"
                className="w-full h-12 rounded-full"
                onClick={handleMessage}
                data-testid="message-artist-button"
              >
                <MessageCircle className="w-4 h-4 mr-2" />
                Message Artist
              </Button>

              <div className="pt-6 border-t space-y-3">
                <div className="text-sm font-semibold text-gray-700 mb-3">Connect</div>
                {artist.instagram_handle && (
                  <a
                    href={`https://instagram.com/${artist.instagram_handle}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center space-x-2 text-gray-600 hover:text-purple-600 transition-colors"
                  >
                    <Instagram className="w-5 h-5" />
                    <span>@{artist.instagram_handle}</span>
                  </a>
                )}
                {artist.youtube_link && (
                  <a
                    href={artist.youtube_link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center space-x-2 text-gray-600 hover:text-purple-600 transition-colors"
                  >
                    <Youtube className="w-5 h-5" />
                    <span>YouTube Channel</span>
                  </a>
                )}
                {artist.facebook_link && (
                  <a
                    href={artist.facebook_link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center space-x-2 text-gray-600 hover:text-purple-600 transition-colors"
                  >
                    <Facebook className="w-5 h-5" />
                    <span>Facebook</span>
                  </a>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ArtistDetail;
